<template>
  <div
    v-if="price"
    class="stripe-form"
  >
    <registration-error v-if="stripeCardErrorMessage">
      <span v-html="stripeCardErrorMessage" />
    </registration-error>

    <div class="price-header">
      <div
        v-if="!submitButtonDisabledPrice"
        class="price-container"
      >
        <div class="price">
          <span
            class="price-hero"
            v-html="finalPrice.exVatDisplay"
          />
          <div
            v-if="discountedFromPrice !== null"
            class="price-details"
          >
            <span class="price-saving">
              {{ displayDiscountedFromPrice }}
            </span>
          </div>
        </div>
        <div
          v-if="price.showTax"
          class="price-tax"
          v-html="finalPrice.incVatDisplay"
        />
      </div>
      <div v-else>
        Checking best price...
      </div>
      <div
        v-if="
          !submitButtonDisabled
            && Object.values(availableBillingPeriods).every(value => value === true)
        "
        class="price-switch-container"
      >
        <div>Monthly</div>
        <label class="switch">
          <input
            :checked="configuration.billingPeriod === 'YEAR'"
            value="YEAR"
            type="checkbox"
            @change="toggleBillingPeriod()"
          >
          <span class="slider round" />
        </label>
        <div>Annual</div>
      </div>
    </div>

    <div class="update-text">
      <span v-if="isAnnual && !merchant">45% off regular monthly pricing.</span> Cancel <span v-if="!merchant && !isAnnual">or upgrade to annual</span> at any time.
    </div>

    <div class="card-container">
      <div id="card-element">
        <!-- a Stripe Element will be inserted here. -->
      </div>
    </div>

    <div class="form-input-group">
      <button
        :disabled="submitButtonDisabled"
        class="btn-register"
        type="button"
        @click="submitStripeForm"
      >
        <span v-html="buttonMessage" />
      </button>
    </div>

    <div
      v-if="!merchant"
      class="small-text"
    >
      You will be charged today, but if Hike isn’t for you please email support@hikeseo.co within the first 14 days for
      a full refund.
    </div>
  </div>
</template>

<script>
import RegistrationError from '../components/RegistrationError.vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import {mapActions, mapGetters} from "vuex";
import {loadStripe} from '@stripe/stripe-js';
import {axiosCatchHandler} from "../../../../js/modules/sharedGlobal";

const CARD_INPUT_STYLE = {
  base: {
    color: '#000000',
    fontFamily: 'DM Sans", Helvetica, sans-serif',
    fontSmoothing: 'antialiased',
    fontSize: '16px',
    '::placeholder': {
      color: '#343a40'
    }
  },
  invalid: {
    color: '#eb1c26',
    iconColor: '#eb1c26'
  },
}

const defaultSubmitButtonMessage = `Get Started - Risk Free<br><small>Love it or get a full refund</small>`;

export default {
  components: {
    RegistrationError,
  },
  props: {
    merchantAccountId: {
      type: String,
      required: false,
      default: null,
    },
    availableBillingPeriods: {
      type: Object,
      required: true,
    },
    preview: {
      type: Boolean,
      default: false,
    },
    merchant: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      stripe: null,
      card: null,
      submitButtonDisabled: false,
      submitButtonDisabledPrice: true,
      stripeCardErrorMessage: null,
      buttonMessage: !this.merchant
        ? defaultSubmitButtonMessage
        : 'Get Started',
      registrationComplete: false,
    }
  },
  computed: {
    ...mapGetters('registration', [
      'price',
      'configuration',
      'name',
      'email',
      'password',
      'stripePaymentMethodId',
      'operationCountry',
      'registrationId',
    ]),
    isAnnual() {
      return this.configuration.billingPeriod === 'YEAR';
    },
    finalPrice() {
      return this.price.discount ? this.price.discount : this.price.fullPrice;
    },
    displayBillingPeriod() {
      return this.price.billingPeriod === 'YEAR' ? 'year' : 'month';
    },
    discountedFromPrice() {
      let priceWithoutDiscount = this.price.discount ? this.price.fullPrice : null;

      if (priceWithoutDiscount !== null) {
        return this.price.currency === 'GBP' ? priceWithoutDiscount?.incVat : priceWithoutDiscount?.exVat;
      }

      return this.price.monthlyCostComparison ?? null;
    },
    displayDiscountedFromPrice() {
      return new Intl.NumberFormat(
        'en-EN',
        { style: 'currency', currency: this.configuration.currency }
      ).format(this.discountedFromPrice / 100);
    },
  },
  watch: {
    async isAnnual() {
      await this.getPrice(
          this.currency,
          this.isAnnual ? 'annual' : 'month',
          this.discountCode,
          this.supportRequired,
      );
    }
  },
  async mounted() {
    await this.getPrice();
    this.submitButtonDisabledPrice = false;
    window.dataLayer = window.dataLayer || [];

    let opts = {};
    if (this.merchantAccountId !== null) {
      opts = { ...opts, stripeAccount: this.merchantAccountId };
    }
    this.stripe = await loadStripe(process.env.MIX_STRIPE_KEY, opts);

    const elements = this.stripe.elements()
    this.card = elements.create('card', {style: CARD_INPUT_STYLE});
    this.card.mount('#card-element');

    this.card.on("change", () => {
      this.stripeCardErrorMessage = event.error ? event.error.message : null;
    });

    if (
        this.billingPeriod &&
        this.billingPeriod === "annual" &&
        !this.discountCode
    ) {
      this.annual = true;
      this.interval = 'year';
    }
  },
  beforeUnmount() {
    if (!this.registrationComplete) {
      this.removeCustomer();
    }
  },
  methods: {
    ...mapActions('registration', [
      'getPrice',
      'toggleBillingPeriod',
      'setStripePaymentMethodId',
      'setRegistrationId',
    ]),
    async submitStripeForm() {
      this.stripeCardErrorMessage = null;
      this.submitButtonDisabled = true;
      this.buttonMessage = `Submitting payment...<br><small>This will only take a few moments!</small>`;

      this.stripe.createPaymentMethod({
        type: 'card',
        card: this.card,
        billing_details: {
          name: this.name
        },
      })
      .then((result) => {
        if (result.error) {
          this.submitButtonDisabled = false;
          this.buttonMessage = defaultSubmitButtonMessage;
        } else {
          if (result && result.paymentMethod) {
            this.setStripePaymentMethodId(result.paymentMethod.id);
            this.createCustomer().then(() => {
              this.completeRegistration();
            }).catch(({ response }) => {
              this.buttonMessage = `Authorising payment...`;
              const { data, status } = response;
              if (status === 422) {
                const { paymentIntent, registrationId } = data;
                this.setRegistrationId(registrationId);
                this.handle3DSecure(paymentIntent);
              }

              if (status === 402) {
                const { message } = data;
                this.submitButtonDisabled = false;
                this.stripeCardErrorMessage = message;
                this.buttonMessage = defaultSubmitButtonMessage;
              }

              if (status === 500) {
                Swal.fire({
                  icon: 'error',
                  html: data.message ?? 'Something went wrong',
                });
                this.stripeCardErrorMessage = 'Something went wrong';
                this.buttonMessage = defaultSubmitButtonMessage;
                this.submitButtonDisabled = false;
              }
            });

            // TODO: Revisit handling customer 3DS verification
          } else {
            this.submitButtonDisabled = false;
            this.buttonMessage = defaultSubmitButtonMessage;
            Swal.fire({
              title: 'Whoops!',
              text: 'Looks like there was a problem taking payment, please contact support@hikeseo.co for '+
                'assistance completing your registration.', // TODO: Remove "Hike".
            });
          }
        }
      })
      .catch(
        (e) => axiosCatchHandler(e, ({response}) => {
          this.submitButtonDisabled = false;
          this.buttonMessage = defaultSubmitButtonMessage;
          const { data } = response;
          this.stripeCardErrorMessage = data.message;
        })
      )
    },
    handle3DSecure(paymentIntent) {
      const { clientSecret, payment_method } = paymentIntent;
      return this.stripe.confirmCardPayment(clientSecret, { payment_method })
        .then((result) => {
          if (result.error) {
            const { message } = result.error;
            this.submitButtonDisabled = false;
            this.stripeCardErrorMessage = message;
            this.buttonMessage = defaultSubmitButtonMessage;
            this.removeCustomer().finally(() => {
              throw result.error;
            });
          } else {
            this.buttonMessage = `Success!<br><small>We're getting things ready for you!</small>`;
            this.completeRegistration();
          }
        })
        .catch(() => {
          this.submitButtonDisabled = false;
          this.stripeCardErrorMessage = 'Sorry, there was a problem taking payment. You have not been charged.';
        });
    },
    completeRegistration() {
      return axios.post('/ajax/complete-registration', {
        registrationId: this.registrationId,
      }).then(response => {
        const { data } = response;

        this.registrationComplete = true;

        window.dataLayer.push({
          'event': 'registerSingle',
        });
        window.location.href = data.url;
      });
    },
    createCustomer() {
      const {
        discountCode,
        billingPeriod,
        currency,
        supportRequired,
      } = this.configuration;

      const toltReferral = window.tolt_referral;

      return axios.post('/ajax/create-customer', {
        customerType: 'SINGLE', // TODO: Resolve weird split between SINGLE/RESELLER
        name: this.name,
        email: this.email,
        password: this.password,
        paymentMethodId: this.stripePaymentMethodId,
        operationCountry: this.operationCountry,
        discountCode,
        billingPeriod,
        currency,
        supportRequired,
        toltReferral,
      }).then(({ data }) => {
        this.setRegistrationId(data.registrationId);
      });
    },
    removeCustomer() {
      const { registrationId } = this;
      return axios.post('/ajax/remove-customer', { registrationId });
    }
  },
};
</script>

<style lang="scss" scoped>
.stripe-form {
  font-family: 'DM Sans', sans-serif;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem;
  background: #FFFFFF;
  border: 1px solid #D8D8D8;
  box-sizing: border-box;
  border-radius: 4px;
  gap: 1.5rem;
}

.btn-register {
  background-color: #4CE0B3;
  color: white;
  text-align: center;
  border: none;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.2s ease;
  font-size: inherit;
  font-family: 'DM Sans', sans-serif;
  padding: 0.5rem;
  border-radius: 4px;

  small {
    font-size: x-small;
  }

  &:hover {
    background-color: #3BB390;
  }

  &:disabled {
    opacity: 0.7;
  }
}

.price-header {
  display: flex;
  width: 100%;
  justify-content: space-between;

  .price-container {
    display: flex;
    flex-direction: column;
    width: 100%;

    .price {
      display: flex;
      width: 100%;

      .price-hero {
        font-family: 'DM Sans', sans-serif;
        font-weight: 700;
        color: #3E3E44;
        overflow: hidden;
        line-height: 1.1;
        font-size: 3rem;
        display: flex;
        flex-direction: column;
        justify-content: space-around;
      }

      .price-details {
        display: flex;
        flex-direction: column;
        padding-top: 0.4rem;

        .no-discount {
          padding-top: 1.25rem;
        }
      }

      .price-saving {
        font-weight: 700;
        text-decoration: line-through;
        padding-left: 0.7rem;
      }
    }
  }

  .update-text {
    color: #3E3E44;
    font-family: DM Sans;
    font-style: normal;
    font-weight: bold;
    font-size: 12px;
    line-height: 16px;
    text-transform: uppercase;
    text-align: center;
  }

  .small-text {
    color: #3E3E44;
    font-family: DM Sans;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .form-input-group > * {
    width: 100%;
    padding: 1rem;
    background: #FFFFFF;
    border: 1px solid #D8D8D8;
    box-sizing: border-box;
    border-radius: 4px;
  }
}

.price-switch-container {
  width: 40%;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.8rem;

  .switch {
    position: relative;
    display: inline-block;
    width: 45px;
    min-width: 45px;
    height: 18px;

    input {
      opacity: 0;
      width: 0;
      height: 0;
    }
  }

  input:checked + .slider {
    background-color: #4CE0B3;
  }

  input:focus + .slider {
    box-shadow: 0 0 1px #4CE0B3;
  }

  input:checked + .slider:before {
    transform: translateX(23px);
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #4CE0B3;
    -webkit-transition: .4s;
    transition: .4s;

    &.round {
      border-radius: 34px;

      &::before {
        position: relative;
        display: block;
        background-color: #fff;
        bottom: 4px;
        content: "";
        height: 14px;
        width: 14px;
        top: 2px;
        left: 4px;
        border-radius: 50%;
        transition: transform 0.2s ease;
      }
    }
  }

  &:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    -webkit-transition: .4s;
    transition: .4s;
  }
}


@media only screen and (max-width: 599px) {
  .price-header {
    flex-direction: column;
    align-items: center;

    .price-container {
      width: auto;
    }
    .price-switch-container {
      padding-top: .5rem;
    }
  }
}

.card-container {
  border: 2px solid #e3e2e2;
  padding: 18px 10px;
}

</style>
